import React from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  ShieldCheckIcon, 
  BoltIcon, 
  CubeTransparentIcon, 
  DocumentTextIcon, 
  CurrencyDollarIcon,
  ClockIcon,
  CloudIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import ValidatorGuide from './components/ValidatorGuide';
import MarketMaking from './components/MarketMaking';
import RiskModels from './components/RiskModels';
import Security from './components/Security';
import Header from './components/Header';

const HomePage: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl pt-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Decentralized LLM-Operated Insurance Built on EigenLayer AVS
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Starting with wildfire insurance in California, we're making disaster insurance transparent, fair, and accessible through the blockchain.
            </motion.p>
            <div className="space-x-4">
              <button 
                onClick={() => alert('Get Insured - Coming Soon!')}
                className="bg-white text-primary-900 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
              >
                Get Insured
              </button>
              <a 
                href="https://andrewgao.notion.site/Eigensurance-19b2c6cd89ad80bfb392e2809af25e71?pvs=74" 
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-block"
              >
                Technical Whitepaper
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Market Size Section */}
      <section className="py-20 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold mb-8"
            >
              Disrupting the $1.9T Insurance Industry
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-secondary-600 mb-12"
            >
              The US property insurance market is ripe for innovation, with outdated systems leaving millions vulnerable.
            </motion.p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-primary-600 mb-2">$164B+</div>
                <p className="text-secondary-600">Estimated losses from 2025 LA wildfires——only 25% insured.)</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-primary-600 mb-2">1.3M</div>
                <p className="text-secondary-600">California homes at high/extreme wildfire risk</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-primary-600 mb-2">34.1%</div>
                <p className="text-secondary-600">Recent rate hike by Allstate, as premiums increase.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Timeline */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">The Growing California Insurance Crisis</h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-200"></div>
              <div className="space-y-16">
                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <div className="text-2xl font-bold text-primary-600">2019</div>
                      <p className="text-secondary-600">State Farm stops accepting new homeowner applications in California</p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="flex items-center">
                    <div className="w-1/2 pr-8"></div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="w-1/2 pl-8">
                      <div className="text-2xl font-bold text-primary-600">2022</div>
                      <p className="text-secondary-600">Average annual premium increases by 68% in high-risk areas</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <div className="text-2xl font-bold text-primary-600">2023</div>
                      <p className="text-secondary-600">Allstate and Farmers suspend new policies, affecting millions</p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="flex items-center">
                    <div className="w-1/2 pr-8"></div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="w-1/2 pl-8">
                      <div className="text-2xl font-bold text-primary-600">2025</div>
                      <p className="text-secondary-600">Millions of families and homes left without access to traditional insurance</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <div className="text-2xl font-bold text-primary-600">January 2025</div>
                      <p className="text-secondary-600">Devastating LA wildfires destroy 18,000+ homes, causing $164B+ in damages with only 25% insured</p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section id="technology" className="py-20 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">The Future of Insurance is Here</h2>
            <p className="text-center text-xl text-secondary-600 mb-16">
              Built on EigenLayer's Actively Validated Services (AVS) network, we're bringing unprecedented transparency, efficiency, and reliability to disaster insurance.
            </p>
            
            <div className="space-y-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BoltIcon className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">EigenLayer Integration</h3>
                  <p className="text-secondary-600">Secured by Ethereum's $50B+ validator network through restaking, ensuring unmatched security and reliability for all insurance operations.</p>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CubeTransparentIcon className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">AI-Powered Operations</h3>
                  <p className="text-secondary-600">Advanced LLMs process claims 95% faster than traditional insurers, with real-time risk assessment using satellite data and weather patterns.</p>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DocumentTextIcon className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Parametric Insurance</h3>
                  <p className="text-secondary-600">Smart contracts automatically trigger payouts within 24 hours of verified disaster conditions, compared to weeks or months with traditional insurance.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Traditional vs. Eigensurance</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl border border-secondary-200">
                <h3 className="text-xl font-semibold mb-6 text-secondary-800">Traditional Insurance</h3>
                <ul className="space-y-4 text-secondary-600">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>27% of premiums ($378B annually) spent on overhead</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Average claim processing time: 30-45 days</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>40% of claims initially denied to maximize profits</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Annual premium increases up to 40% in high-risk areas</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Complex policies with average 40-page documents</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>No coverage options for 2.4M high-risk homes</span>
                  </li>
                </ul>
              </div>
              <div className="bg-primary-50 p-8 rounded-xl border border-primary-200">
                <h3 className="text-xl font-semibold mb-6 text-primary-800">Eigensurance</h3>
                <ul className="space-y-4 text-primary-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Only 3% operational costs through AI automation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>24-hour guaranteed payouts for verified claims</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>100% automated, bias-free claims processing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Dynamic pricing updated daily based on risk</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Simple, transparent smart contracts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Available to all California homeowners</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const App: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/security" element={<Security />} />
        <Route path="/validator-guide" element={<ValidatorGuide />} />
        <Route path="/market-making" element={<MarketMaking />} />
        <Route path="/risk-models" element={<RiskModels />} />
      </Routes>

      {/* Footer */}
      <footer className="bg-secondary-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Eigensurance</h3>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://andrewgao.notion.site/Eigensurance-19b2c6cd89ad80bfb392e2809af25e71?pvs=74" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Whitepaper
                  </a>
                </li>
                <li>
                  <Link 
                    to="/security" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Security
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/validator-guide" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Validator Guide
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/market-making" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Market Making
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/risk-models" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Risk Models
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">EigenLayer Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://www.eigenlayer.xyz/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    EigenLayer
                  </a>
                </li>
                <li>
                  <a 
                    href="https://docs.eigenlayer.xyz/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Documentation
                  </a>
                </li>
                <li>
                  <a 
                    href="https://docs.eigenlayer.xyz/eigenlayer/restaking-guides/overview" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Restaking Guide
                  </a>
                </li>
                <li>
                  <a 
                    href="https://docs.eigenlayer.xyz/eigenlayer/operator-guides/operator-introduction" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    Operator Guide
                  </a>
                </li>
                <li>
                  <a 
                    href="https://docs.eigenda.xyz/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-400 hover:text-white"
                  >
                    EigenDA
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl mb-4">
                <Link to="/" className="hover:text-white">
                  <span className="font-bold">Eigen</span>
                  <span className="font-normal">surance</span>
                </Link>
              </h3>
              <p className="text-secondary-400 mb-4">Building the future of decentralized parametric insurance.</p>
              <p className="text-secondary-400">
                Powered by{' '}
                <a 
                  href="https://www.eigenlayer.xyz/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-400 hover:text-white"
                >
                  EigenLayer
                </a>
              </p>
            </div>
          </div>
          <div className="border-t border-secondary-800 mt-12 pt-8 text-center text-secondary-400">
            © 2025 Eigensurance Protocol
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App; 